// Document-node REST helpers — Account- and PlanYear-scoped
// virtual filesystem.

import { api } from "@/api/client";


export interface DocumentNodeFolder {
  name: string;
  path: string;
  is_marker: boolean;
  node_id?: string;
}


export interface DocumentNodeFile {
  node_id: string;
  path: string;
  filename: string;
  file_id: string | null;
  content_type: string | null;
  size_bytes: number | null;
  is_client_visible: boolean;
  file_type: string | null;
  file_type_other: string | null;
  line_of_coverage: string | null;
  line_of_coverage_other: string | null;
  uploaded_at: string | null;
  uploaded_by_id: string | null;
}


export interface DocumentLibraryListing {
  folder: string;
  folders: DocumentNodeFolder[];
  files: DocumentNodeFile[];
}


export interface DocumentHost {
  /** ``account`` for Account-scoped, ``plan_year`` for PlanYear. */
  hostKind: "account" | "plan_year";
  accountId: string;
  planYearId?: string | null;
}


function basePath(host: DocumentHost): string {
  return host.hostKind === "account"
    ? `/api/v1/accounts/${encodeURIComponent(host.accountId)}/documents`
    : `/api/v1/accounts/${encodeURIComponent(host.accountId)}`
      + `/plan_years/${encodeURIComponent(host.planYearId ?? "")}`
      + "/documents";
}


export function listDocuments(
  host: DocumentHost,
  folder: string,
  opts?: { signal?: AbortSignal },
): Promise<DocumentLibraryListing> {
  const qs = folder && folder !== "/"
    ? `?folder=${encodeURIComponent(folder)}` : "";
  return api.get<DocumentLibraryListing>(`${basePath(host)}/${qs}`, opts);
}


export function createDocumentFolder(
  host: DocumentHost, folder: string,
): Promise<{ node_id: string; path: string }> {
  return api.post<{ node_id: string; path: string }>(
    `${basePath(host)}/folders/`, { folder },
  );
}


export interface PresignResponse {
  upload_url: string;
  storage_key: string;
  storage_url: string;
  expires_in_seconds: number;
  headers: Record<string, string>;
}


export function presignDocumentUpload(
  host: DocumentHost,
  payload: {
    filename: string;
    content_type: string;
    size_bytes: number;
    hash_sha256_hex: string;
  },
): Promise<PresignResponse> {
  return api.post<PresignResponse>(
    `${basePath(host)}/upload/presign`, payload,
  );
}


export interface FinalizeResponse {
  node_id: string;
  path: string;
  file_id: string;
  is_dedup: boolean;
  filename: string;
  content_type: string;
  size_bytes: number;
}


export function finalizeDocumentUpload(
  host: DocumentHost,
  payload: {
    folder: string;
    filename: string;
    content_type: string;
    size_bytes: number;
    storage_key: string;
    hash_sha256_hex?: string;
    file_type?: string | null;
    line_of_coverage?: string | null;
    is_client_visible?: boolean;
  },
): Promise<FinalizeResponse> {
  return api.post<FinalizeResponse>(
    `${basePath(host)}/upload/finalize`, payload,
  );
}


export function deleteDocumentNode(
  host: DocumentHost, nodeId: string,
): Promise<{ deleted: boolean; node_id: string }> {
  return api.delete<{ deleted: boolean; node_id: string }>(
    `${basePath(host)}/${encodeURIComponent(nodeId)}`,
  );
}


export interface DocumentPreviewResponse {
  url: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}


export function previewDocumentNode(
  host: DocumentHost, nodeId: string,
): Promise<DocumentPreviewResponse> {
  return api.get<DocumentPreviewResponse>(
    `${basePath(host)}/${encodeURIComponent(nodeId)}/preview`,
  );
}


export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}


export async function uploadFileViaPresign(
  host: DocumentHost,
  file: File,
  folder: string,
): Promise<FinalizeResponse> {
  const hash = await sha256Hex(file);
  const presign = await presignDocumentUpload(host, {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size_bytes: file.size,
    hash_sha256_hex: hash,
  });
  const putRes = await fetch(presign.upload_url, {
    method: "PUT",
    headers: presign.headers,
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(
      `upload failed: ${putRes.status} ${putRes.statusText}`,
    );
  }
  return finalizeDocumentUpload(host, {
    folder,
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size_bytes: file.size,
    storage_key: presign.storage_key,
    hash_sha256_hex: hash,
  });
}
